/**
 * ImportWizard.jsx - Assistente de Importação Excel
 * CG Business ERP - P6
 * 
 * BLUEPRINT FINAL:
 * - 2 Sheets: PRODUCTS e ITEMS
 * - 3 Steps: Upload → Preview → Resultado
 * - Preview com badges CREATE/UPDATE/ERROR
 * - Botão "Exportar Erros"
 * - Commit bloqueado se existir QUALQUER erro
 * - Rollback apenas Admin
 */

import { useEffect, useState, useCallback } from 'react';
import {
  FileUp, FileSpreadsheet, Download, AlertCircle, CheckCircle2,
  XCircle, Loader2, Upload, ChevronRight, RotateCcw, Info,
  RefreshCw, FileWarning, Package, Layers
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { getStoredAuthToken } from '@/lib/authStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Steps
const STEPS = [
  { id: 1, name: 'Upload', description: 'Carregar ficheiro' },
  { id: 2, name: 'Preview', description: 'Validação' },
  { id: 3, name: 'Resultado', description: 'Conclusão' },
];

export function ImportWizard({ 
  entity = 'products', 
  categories = [],
  defaultCategoryId = 'all',
  open, 
  onOpenChange,
  onSuccess 
}) {
  const { hasRole } = useAuth();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [activePreviewTab, setActivePreviewTab] = useState('products');
  const [sheetMode, setSheetMode] = useState('products');
  const [selectedCategoryId, setSelectedCategoryId] = useState(defaultCategoryId || 'all');
  const [rollbackJustification, setRollbackJustification] = useState('');
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  
  const canRollback = hasRole(['admin']);
  const selectedCategory = categories.find((category) => category.id === selectedCategoryId) || null;
  const effectiveCategoryId = selectedCategoryId && selectedCategoryId !== 'all' ? selectedCategoryId : null;
  const isProductsMode = sheetMode === 'products';
  const isItemsMode = sheetMode === 'items';

  useEffect(() => {
    if (!open) return;
    setSelectedCategoryId(defaultCategoryId || 'all');
  }, [defaultCategoryId, open]);
  
  // Get auth token
  const getToken = () => getStoredAuthToken();
  
  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const params = new URLSearchParams({ sheet_mode: sheetMode });
      if (effectiveCategoryId) {
        params.set('category_id', effectiveCategoryId);
      }

      const response = await fetch(`${API_URL}/import/templates/${entity}?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Erro ao descarregar template');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `template_${sheetMode}_${selectedCategory?.base_prefix || 'all'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template descarregado!');
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Apenas ficheiros Excel (.xlsx) são aceites');
        return;
      }
      
      // Check file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('Ficheiro excede o limite de 10MB');
        return;
      }
      
      setFile(selectedFile);
      setPreview(null);
      setResult(null);
    }
  };
  
  // Preview import
  const handlePreview = async () => {
    if (!file) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sheet_mode', sheetMode);
      if (effectiveCategoryId) {
        formData.append('category_id', effectiveCategoryId);
      }
      
      const response = await fetch(`${API_URL}/import/${entity}/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erro na validação');
      }
      
      setPreview(data);
      setStep(2);
      
      if (sheetMode === 'items') {
        setActivePreviewTab('items');
      } else if (sheetMode === 'products') {
        setActivePreviewTab('products');
      } else if (data.items?.total > 0 && data.products?.total === 0) {
        setActivePreviewTab('items');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Export errors to Excel
  const handleExportErrors = async () => {
    if (!preview) return;
    
    try {
      const response = await fetch(`${API_URL}/import/export-errors`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preview)
      });
      
      if (!response.ok) throw new Error('Erro ao exportar erros');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `erros_importacao.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Erros exportados!');
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  // Execute import
  const handleImport = async () => {
    if (!file || !preview?.can_import) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sheet_mode', sheetMode);
      if (effectiveCategoryId) {
        formData.append('category_id', effectiveCategoryId);
      }
      
      const response = await fetch(`${API_URL}/import/${entity}/execute`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erro na importação');
      }
      
      setResult(data);
      setStep(3);
      
      const summary = data.summary || {};
      if (summary.total_errors === 0) {
        toast.success(`Importação concluída: ${summary.products_created || 0} produtos criados, ${summary.products_updated || 0} actualizados, ${summary.items_created || 0} items`);
      } else {
        toast.warning(`Importação com erros: ${summary.products_created || 0} criados, ${summary.total_errors || 0} erros`);
      }
      
      onSuccess?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Rollback import
  const handleRollback = async () => {
    if (!result?.import_id || !canRollback) return;
    
    if (!rollbackJustification || rollbackJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/import/sessions/${result.import_id}/rollback?justification=${encodeURIComponent(rollbackJustification.trim())}`, 
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erro no rollback');
      }
      
      const summary = data.summary || {};
      toast.success(`Rollback concluído: ${summary.products_reverted || 0} produtos, ${summary.items_reverted || 0} items revertidos`);
      setResult(prev => ({ ...prev, rolled_back: true }));
      setShowRollbackDialog(false);
      setRollbackJustification('');
      onSuccess?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Reset wizard
  const handleReset = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setResult(null);
    setActivePreviewTab('products');
    setSheetMode('products');
    setSelectedCategoryId(defaultCategoryId || 'all');
    setRollbackJustification('');
  };
  
  // Close dialog
  const handleClose = () => {
    handleReset();
    onOpenChange(false);
  };
  
  // Get badge for action type
  const getActionBadge = (action, isValid) => {
    if (!isValid) {
      return <Badge variant="destructive" className="text-xs">ERRO</Badge>;
    }
    if (action === 'create') {
      return <Badge className="bg-green-100 text-green-700 text-xs">CRIAR</Badge>;
    }
    if (action === 'update') {
      return <Badge className="bg-blue-100 text-blue-700 text-xs">ACTUALIZAR</Badge>;
    }
    return null;
  };
  
  // Count totals
  const getTotals = () => {
    if (!preview) return { toCreate: 0, toUpdate: 0, errors: 0 };
    
    const products = preview.products || {};
    const items = preview.items || {};
    
    return {
      toCreate: (products.to_create || 0) + (items.to_create || 0),
      toUpdate: products.to_update || 0,
      errors: (products.errors || 0) + (items.errors || 0)
    };
  };
  
  const totals = getTotals();
  
  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Importar Inventário via Excel
            </DialogTitle>
            <DialogDescription>
              Importe produtos e items por categoria, mantendo a mesma sequência mas com execução separada
            </DialogDescription>
          </DialogHeader>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 py-4 border-b">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="flex items-center">
                <div className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm",
                  step === s.id && "bg-primary text-primary-foreground",
                  step > s.id && "bg-green-100 text-green-700",
                  step < s.id && "bg-muted text-muted-foreground"
                )}>
                  {step > s.id ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                      {s.id}
                    </span>
                  )}
                  <span className="font-medium">{s.name}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto py-4">
            {/* Step 1: Upload */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Template download */}
                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertTitle className="text-blue-800">Escolha o modo e a categoria antes de descarregar a template</AlertTitle>
                  <AlertDescription className="flex items-center justify-between text-blue-700">
                    <div>
                      <p><strong>Produtos:</strong> catálogo, categorias, subcategorias e especificações técnicas</p>
                      <p><strong>Items:</strong> séries/IMEIs e lotes para artigos rastreáveis</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="ml-4" data-testid="download-template-button">
                      <Download className="h-4 w-4 mr-2" />
                      Descarregar Template
                    </Button>
                  </AlertDescription>
                </Alert>

                <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
                  <Card data-testid="inventory-import-mode-card">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Modo de importação</CardTitle>
                      <CardDescription>Importe produtos primeiro e items depois, ou use o modo completo quando precisar.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-2 sm:grid-cols-3">
                        {[
                          { value: 'products', label: 'Produtos', helper: 'Catálogo e subcategorias' },
                          { value: 'items', label: 'Items', helper: 'Séries / IMEIs / lotes' },
                          { value: 'both', label: 'Completo', helper: 'Produtos + Items' },
                        ].map((mode) => (
                          <Button
                            key={mode.value}
                            type="button"
                            variant={sheetMode === mode.value ? 'default' : 'outline'}
                            className={cn(
                              'h-auto min-h-[82px] flex-col items-start justify-start gap-1 rounded-2xl px-4 py-3 text-left',
                              sheetMode === mode.value ? 'bg-slate-900 text-white hover:bg-slate-800' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                            )}
                            onClick={() => setSheetMode(mode.value)}
                            data-testid={`inventory-import-mode-${mode.value}`}
                          >
                            <span className="text-sm font-semibold">{mode.label}</span>
                            <span className="text-xs opacity-80">{mode.helper}</span>
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card data-testid="inventory-import-category-card">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Categoria alvo</CardTitle>
                      <CardDescription>Restrinja a template e a validação a uma categoria para importar por blocos menores.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Label htmlFor="inventory-import-category-select">Categoria</Label>
                      <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                        <SelectTrigger id="inventory-import-category-select" data-testid="inventory-import-category-select">
                          <SelectValue placeholder="Todas as categorias" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas as categorias</SelectItem>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              <span className="mr-2 font-mono text-[11px] text-slate-500">{category.base_prefix}</span>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground" data-testid="inventory-import-category-helper">
                        {selectedCategory ? `Escopo actual: ${selectedCategory.name} (${selectedCategory.base_prefix})` : 'Escopo actual: todas as categorias activas'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* File upload */}
                <div className="space-y-2">
                  <Label>Ficheiro Excel</Label>
                  <div 
                    className={cn(
                      "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                      file ? "border-green-500 bg-green-50" : "border-muted-foreground/25 hover:border-primary"
                    )}
                    onClick={() => document.getElementById('import-file-input')?.click()}
                    data-testid="file-drop-zone"
                  >
                    <input
                      id="import-file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-input"
                    />
                    
                    {file ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle2 className="h-10 w-10 text-green-600" />
                        <p className="font-medium">{file.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                          Alterar ficheiro
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-10 w-10 text-muted-foreground" />
                        <p className="font-medium">Clique para seleccionar ou arraste o ficheiro</p>
                        <p className="text-sm text-muted-foreground">
                          Apenas ficheiros .xlsx (máx. 10MB, 1000 linhas por sheet)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Instructions */}
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="h-4 w-4 text-green-600" />
                        Sheet PRODUCTS
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-xs text-muted-foreground space-y-1">
                      <p>• <strong>Modo recomendado:</strong> importar primeiro por categoria</p>
                      <p>• <strong>Código vazio:</strong> gera SKU automático</p>
                      <p>• <strong>Código preenchido:</strong> usa o SKU informado ou actualiza se já existir</p>
                      <p>• <strong>Specs:</strong> aceitam JSON ou pares chave=valor</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Layers className="h-4 w-4 text-blue-600" />
                        Sheet ITEMS
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-xs text-muted-foreground space-y-1">
                      <p>• <strong>Modo recomendado:</strong> importar depois de confirmar os produtos</p>
                      <p>• <strong>Série/IMEI ou lote</strong> obrigatório</p>
                      <p>• <strong>Lotes</strong> suportam quantidade &gt; 1</p>
                      <p>• <strong>Produto</strong> pode ser referenciado por SKU/código ou nome exacto</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
            
            {/* Step 2: Preview */}
            {step === 2 && preview && (
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold">{(preview.products?.total || 0) + (preview.items?.total || 0)}</div>
                      <p className="text-sm text-muted-foreground">Total de Linhas</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-green-600">{totals.toCreate}</div>
                      <p className="text-sm text-muted-foreground">A Criar</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-blue-600">{totals.toUpdate}</div>
                      <p className="text-sm text-muted-foreground">A Actualizar</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className={cn("text-2xl font-bold", totals.errors > 0 ? "text-red-600" : "text-green-600")}>
                        {totals.errors}
                      </div>
                      <p className="text-sm text-muted-foreground">Com Erros</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Status message */}
                {preview.can_import ? (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800">Pronto para Importar</AlertTitle>
                    <AlertDescription className="text-green-700">
                      Todos os dados foram validados com sucesso. Confirme para executar a importação{preview.selected_category_name ? ` na categoria ${preview.selected_category_name}` : ''}.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Erros Encontrados - Importação Bloqueada</AlertTitle>
                    <AlertDescription className="flex items-center justify-between">
                      <span>Corrija TODOS os erros antes de importar</span>
                      <Button variant="outline" size="sm" onClick={handleExportErrors} className="ml-4">
                        <FileWarning className="h-4 w-4 mr-2" />
                        Exportar Erros
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Preview tabs */}
                <Tabs value={activePreviewTab} onValueChange={setActivePreviewTab}>
                  <TabsList>
                    {!isItemsMode && (
                      <TabsTrigger value="products" className="flex items-center gap-2" data-testid="inventory-import-preview-products-tab">
                        <Package className="h-4 w-4" />
                        Produtos ({preview.products?.total || 0})
                      </TabsTrigger>
                    )}
                    {!isProductsMode && (
                      <TabsTrigger value="items" className="flex items-center gap-2" data-testid="inventory-import-preview-items-tab">
                        <Layers className="h-4 w-4" />
                        Items ({preview.items?.total || 0})
                      </TabsTrigger>
                    )}
                  </TabsList>
                  
                  {/* Products Tab */}
                  {!isItemsMode && <TabsContent value="products">
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-64 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-16">Linha</TableHead>
                              <TableHead className="w-24">Acção</TableHead>
                              <TableHead>Código</TableHead>
                              <TableHead>Nome</TableHead>
                              <TableHead>Categoria</TableHead>
                              <TableHead>Preço</TableHead>
                              <TableHead>Mensagem</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(preview.products?.rows || []).map((row) => (
                              <TableRow 
                                key={row.row_number}
                                className={cn(!row.is_valid && "bg-red-50")}
                                data-testid={`preview-row-${row.row_number}`}
                              >
                                <TableCell className="font-mono text-xs">{row.row_number}</TableCell>
                                <TableCell>{getActionBadge(row.action, row.is_valid)}</TableCell>
                                <TableCell className="font-mono text-xs">{row.data.code || '(novo)'}</TableCell>
                                <TableCell className="max-w-[150px] truncate">{row.data.name || '-'}</TableCell>
                                <TableCell>{row.data.category_code || '-'}</TableCell>
                                <TableCell>{row.data.sale_price?.toLocaleString() || '-'}</TableCell>
                                <TableCell>
                                  {row.is_valid ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <span className="text-xs text-red-600" title={row.errors.map(e => e.message).join(', ')}>
                                      {row.errors[0]?.message}
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                            {(!preview.products?.rows || preview.products.rows.length === 0) && (
                              <TableRow>
                                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                  Nenhum produto no ficheiro
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </TabsContent>}
                  
                  {/* Items Tab */}
                  {!isProductsMode && <TabsContent value="items">
                    <div className="border rounded-lg overflow-hidden">
                      <div className="max-h-64 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-16">Linha</TableHead>
                              <TableHead className="w-24">Acção</TableHead>
                              <TableHead>Produto</TableHead>
                              <TableHead>IMEI/SN</TableHead>
                              <TableHead>Localização</TableHead>
                              <TableHead>Estado</TableHead>
                              <TableHead>Mensagem</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(preview.items?.rows || []).map((row) => (
                              <TableRow 
                                key={row.row_number}
                                className={cn(!row.is_valid && "bg-red-50")}
                              >
                                <TableCell className="font-mono text-xs">{row.row_number}</TableCell>
                                <TableCell>{getActionBadge(row.action, row.is_valid)}</TableCell>
                                <TableCell className="font-mono text-xs">{row.data.product_code || '-'}</TableCell>
                                <TableCell className="font-mono text-xs">{row.data.serial_or_imei || '-'}</TableCell>
                                <TableCell>{row.data.location_id || '-'}</TableCell>
                                <TableCell>{row.data.state || 'in_stock'}</TableCell>
                                <TableCell>
                                  {row.is_valid ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <span className="text-xs text-red-600" title={row.errors.map(e => e.message).join(', ')}>
                                      {row.errors[0]?.message}
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                            {(!preview.items?.rows || preview.items.rows.length === 0) && (
                              <TableRow>
                                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                  Nenhum item no ficheiro (sheet ITEMS)
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </TabsContent>}
                </Tabs>
                
                {/* Confirmation summary */}
                {preview.can_import && (
                  <Card className="bg-slate-50">
                    <CardContent className="pt-4">
                      <p className="font-medium mb-2">O que vai acontecer:</p>
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        {!isItemsMode && <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                          <span>Criar {preview.products?.to_create || 0} produtos</span>
                        </div>}
                        {!isItemsMode && <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500" />
                          <span>Actualizar {preview.products?.to_update || 0}</span>
                        </div>}
                        {!isProductsMode && <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-indigo-500" />
                          <span>Criar {preview.items?.to_create || 0} items</span>
                        </div>}
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-gray-300" />
                          <span>{preview.selected_category_name || 'Todas as categorias'}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            
            {/* Step 3: Result */}
            {step === 3 && result && (
              <div className="space-y-6">
                {/* Success/Warning message */}
                {(result.summary?.total_errors || 0) === 0 ? (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <AlertTitle className="text-green-800">Importação Concluída com Sucesso!</AlertTitle>
                    <AlertDescription className="text-green-700">
                      {result.summary?.products_created || 0} produto(s) criado(s), {result.summary?.products_updated || 0} actualizado(s), {result.summary?.items_created || 0} item(s)
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive">
                    <AlertCircle className="h-5 w-5" />
                    <AlertTitle>Importação Concluída com Erros</AlertTitle>
                    <AlertDescription>
                      {result.summary?.products_created || 0} criado(s), {result.summary?.products_updated || 0} actualizado(s), {result.summary?.total_errors || 0} erro(s)
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Summary card */}
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-3xl font-bold text-green-600">{result.summary?.products_created || 0}</div>
                      <p className="text-sm text-muted-foreground">Produtos Criados</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">{result.summary?.products_updated || 0}</div>
                      <p className="text-sm text-muted-foreground">Produtos Actualizados</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-3xl font-bold text-indigo-600">{result.summary?.items_created || 0}</div>
                      <p className="text-sm text-muted-foreground">Items Criados</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className={cn("text-3xl font-bold", (result.summary?.total_errors || 0) > 0 ? "text-red-600" : "text-gray-400")}>
                        {result.summary?.total_errors || 0}
                      </div>
                      <p className="text-sm text-muted-foreground">Erros</p>
                    </CardContent>
                  </Card>
                </div>

                <p className="text-sm text-muted-foreground" data-testid="inventory-import-result-scope">
                  Escopo aplicado: {result.summary?.selected_category_name || selectedCategory?.name || 'Todas as categorias'} · modo {result.summary?.sheet_mode || sheetMode}
                </p>
                
                {/* Import session info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Sessão de Importação</CardTitle>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between">
                    <div>
                      <code className="bg-muted px-2 py-1 rounded text-sm">{result.import_id}</code>
                      <p className="text-xs text-muted-foreground mt-1">
                        Guarde este código para referência ou rollback
                      </p>
                    </div>
                    {canRollback && !result.rolled_back && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowRollbackDialog(true)}
                        disabled={loading}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reverter Importação
                      </Button>
                    )}
                    {result.rolled_back && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700">
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Revertido
                      </Badge>
                    )}
                  </CardContent>
                </Card>
                
                {/* Error details */}
                {result.errors?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm text-red-600">Detalhes dos Erros</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {result.errors.map((err, idx) => (
                          <div key={idx} className="text-sm text-red-600 flex items-start gap-2">
                            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <span>
                              {err.sheet && <span className="font-medium">[{err.sheet}] </span>}
                              Linha {err.row}: {err.error}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
          
          {/* Footer Actions */}
          <DialogFooter className="border-t pt-4">
            {step === 1 && (
              <>
                <Button variant="outline" onClick={handleClose}>Cancelar</Button>
                <Button onClick={handlePreview} disabled={!file || loading} data-testid="preview-button">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Validar e Pré-visualizar
                </Button>
              </>
            )}
            
            {step === 2 && (
              <>
                <Button variant="outline" onClick={() => setStep(1)}>Voltar</Button>
                <Button 
                  onClick={handleImport} 
                  disabled={!preview?.can_import || loading}
                  data-testid="import-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Confirmar Importação
                </Button>
              </>
            )}
            
            {step === 3 && (
              <>
                <Button variant="outline" onClick={handleReset}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Nova Importação
                </Button>
                <Button onClick={handleClose}>Concluir</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Rollback Confirmation Dialog */}
      <Dialog open={showRollbackDialog} onOpenChange={setShowRollbackDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Confirmar Rollback
            </DialogTitle>
            <DialogDescription>
              Esta acção irá reverter TODAS as alterações desta importação. Esta acção não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rollback-justification">Justificação *</Label>
              <Textarea
                id="rollback-justification"
                placeholder="Explique o motivo do rollback (mínimo 5 caracteres)"
                value={rollbackJustification}
                onChange={(e) => setRollbackJustification(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRollbackDialog(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRollback}
              disabled={loading || rollbackJustification.trim().length < 5}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirmar Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ImportWizard;
